import React, { type ForwardedRef, forwardRef, type ReactNode, type Ref } from 'react';

import { InputText } from '@pkg/primitives';

import {
    type FieldTextHandler,
    type FieldTextSize,
    type FieldTextTheme,
    type InputTextSubset,
} from './FieldText.types';

import { Label, LabelWrapper, Prefix, StyledFieldText, Suffix } from './FieldText.style';

export interface FieldTextProps extends InputTextSubset {
    inputRef?: Ref<HTMLInputElement>;
    popoverProps?: Record<string, unknown>;
    label?: string;
    prefix?: ReactNode;
    prefixOffset?: number;
    prefixPassthrough?: boolean;
    suffix?: ReactNode;
    suffixOffset?: number;
    suffixPassthrough?: boolean;
    size?: FieldTextSize;
    theme?: FieldTextTheme;
    backdropBlur?: boolean;
    nativeErrorStyling?: boolean;
    tabularNums?: boolean;
    onFocus?: FieldTextHandler;
    onBlur?: FieldTextHandler;
    onChange?: FieldTextHandler;

    // TODO: Support these props after consolidating
    // `EmojiPickerCombo` and `EmojiPickerComboFloating`.
    // See `FieldSearch / GlobalSearch` for implementation.
    // onClear?: FieldTextHandler;
    // onSelectEmoji?: EmojiPickerProps['onSelectEmoji'];

    // TODO: Consider supporting a specific set of `radius` variants.
    // Ex: `'none' | 'tight' | 'loose' | 'full'`
}

function FieldTextComponent(
    {
        inputRef,
        popoverProps,
        label,
        prefix,
        prefixOffset = 0,
        prefixPassthrough = false,
        suffix,
        suffixOffset = 0,
        suffixPassthrough = false,
        size = 'medium',
        theme = 'dark',
        backdropBlur = false,
        nativeErrorStyling = false,
        tabularNums = false,
        onFocus,
        onBlur,
        onChange,
        // Unadulterated `InputText` props.
        id,
        value,
        placeholder,
        ...inputProps
    }: FieldTextProps,
    ref: ForwardedRef<HTMLDivElement>,
) {
    const smallerSize = size === 'tiny' || size === 'small';
    const canRenderLabel = Boolean(label && !smallerSize);
    const hasInput = Boolean(value?.toString().length);

    const labelMarkup = canRenderLabel ? (
        <LabelWrapper>
            <Label htmlFor={id}>{label}</Label>
        </LabelWrapper>
    ) : null;

    const prefixMarkup = prefix ? <Prefix $passthrough={prefixPassthrough}>{prefix}</Prefix> : null;
    const suffixMarkup = suffix ? <Suffix $passthrough={suffixPassthrough}>{suffix}</Suffix> : null;

    return (
        <StyledFieldText
            ref={ref}
            $prefixOffset={prefixOffset}
            $suffixOffset={suffixOffset}
            $size={size}
            $theme={theme}
            $backdropBlur={backdropBlur}
            $hasInput={hasInput}
            $nativeErrorStyling={nativeErrorStyling}
            $tabularNums={tabularNums}
            {...popoverProps}
        >
            <InputText
                {...inputProps}
                ref={inputRef}
                id={id}
                value={value}
                placeholder={canRenderLabel ? undefined : placeholder}
                onBlur={onBlur ? ({ target }) => onBlur(target.value, id) : undefined}
                onFocus={onFocus ? ({ target }) => onFocus(target.value, id) : undefined}
                onChange={onChange ? ({ target }) => onChange(target.value, id) : undefined}
            />

            {labelMarkup}
            {prefixMarkup}
            {suffixMarkup}
        </StyledFieldText>
    );
}

export const FieldText = forwardRef(FieldTextComponent);
